import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { type Dayjs } from 'dayjs';
import { Calendar } from 'lucide-react';
import React, { useState } from 'react';
import { formatDateRange, getDateRangeShortcut } from 'shared/utils/dateUtils';

import {
  CalendarDateButton,
  CalendarIcon,
  CalendarPopoverContent,
  CalendarSection,
  CompactCalendar,
  CompactCalendarContent,
  CompactCalendarPopover,
  DateShortcutButton,
  DateShortcutsContainer,
  RangePickersDay,
} from './DateRangePicker.styles';

interface Props {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  shortcuts?: string[];
  onDateClick?: (date: string) => void; // override for custom date selection behaviour (e.g. weekly snap)
  heromode?: boolean;
}

const DEFAULT_SHORTCUTS = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'lastTwoWeeks',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
];

const SHORTCUT_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  lastWeek: 'Last week',
  lastTwoWeeks: 'Past two weeks',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  thisYear: 'This year',
  lastYear: 'Last year',
};

const DateRangePicker: React.FC<Props> = ({
  startDate,
  endDate,
  onChange,
  shortcuts = DEFAULT_SHORTCUTS,
  onDateClick,
  heromode,
}) => {
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLButtonElement | null>(null);
  const [selectingStart, setSelectingStart] = useState(true);

  const handleDateClick = (clickedDate: string) => {
    if (onDateClick) {
      onDateClick(clickedDate);
      return;
    }
    if (selectingStart) {
      onChange(clickedDate, clickedDate);
      setSelectingStart(false);
    } else {
      if (dayjs(clickedDate).isBefore(dayjs(startDate))) {
        onChange(clickedDate, startDate);
      } else {
        onChange(startDate, clickedDate);
      }
      setSelectingStart(true);
      setCalendarAnchor(null);
    }
  };

  const handleShortcutClick = (shortcut: string) => {
    const range = getDateRangeShortcut(shortcut);
    onChange(range.start, range.end);
    setSelectingStart(true);
    setCalendarAnchor(null);
  };

  const handlePrev = () => {
    onChange(
      dayjs(startDate).subtract(1, 'week').format('YYYY-MM-DD'),
      dayjs(endDate).subtract(1, 'week').format('YYYY-MM-DD'),
    );
  };

  const handleNext = () => {
    onChange(
      dayjs(startDate).add(1, 'week').format('YYYY-MM-DD'),
      dayjs(endDate).add(1, 'week').format('YYYY-MM-DD'),
    );
  };

  return (
    <>
      <CalendarSection heromode={heromode}>
        <IconButton size="small" onClick={handlePrev}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <CalendarDateButton
          component="button"
          heromode={heromode}
          onClick={(e: React.MouseEvent) => setCalendarAnchor(e.currentTarget as HTMLButtonElement)}
        >
          <CalendarIcon>
            <Calendar size={14} />
          </CalendarIcon>
          {formatDateRange(startDate, endDate)}
        </CalendarDateButton>
        <IconButton size="small" onClick={handleNext}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </CalendarSection>

      <CompactCalendarPopover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CalendarPopoverContent>
            <DateShortcutsContainer>
              {shortcuts.map((key) => (
                <DateShortcutButton key={key} onClick={() => handleShortcutClick(key)}>
                  {SHORTCUT_LABELS[key] ?? key}
                </DateShortcutButton>
              ))}
            </DateShortcutsContainer>
            <CompactCalendarContent>
              <CompactCalendar startdate={startDate} enddate={endDate}>
                <DateCalendar
                  value={dayjs(selectingStart ? startDate : endDate)}
                  onChange={(newValue: Dayjs | null) => {
                    if (newValue) handleDateClick(newValue.format('YYYY-MM-DD'));
                  }}
                  slots={{
                    day: (dayProps) => {
                      const date = dayProps.day;
                      const isSelected =
                        date.isSame(dayjs(startDate), 'day') || date.isSame(dayjs(endDate), 'day');
                      const isInRange =
                        date.isAfter(dayjs(startDate), 'day') &&
                        date.isBefore(dayjs(endDate), 'day');
                      return (
                        <RangePickersDay
                          {...dayProps}
                          isselected={isSelected}
                          isinrange={isInRange}
                        />
                      );
                    },
                  }}
                />
              </CompactCalendar>
            </CompactCalendarContent>
          </CalendarPopoverContent>
        </LocalizationProvider>
      </CompactCalendarPopover>
    </>
  );
};

export default DateRangePicker;
